import React, { useState, useEffect } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { deleteTableColumnsAction } from 'components/spreadsheet/contexts/data/actions'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { ITableViewWithColumns } from 'types'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import { HUB_BASE } from 'routes'
import Skeleton from 'components/skeleton'

export interface DeleteColumnModalProps extends ModalProps {
  columnIds: string[]
}

const DeleteColumnModal: React.FC<DeleteColumnModalProps> = ({ id, open, setOpen, columnIds }) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData, clearColumnSelection } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const [loadingViewUsedColumns, setLoadingViewUsedColumns] = useState<boolean>(true)
  const [processing, setProcessing] = useState<boolean>(false)
  const [usedViews, setUsedViews] = useState<ITableViewWithColumns[]>([])

  const count = columnIds.length
  const isSingle = count === 1
  const tableId = spreadsheetData.tableDetails.publicId
  // A stable string key of the ids, so the fetch effect depends on the *contents* of the selection
  // rather than the array's identity (callers may pass a fresh array on every render).
  const columnIdsKey = columnIds.join(',')

  const handleClose = () => {
    setProcessing(false)
    setOpen(false)
  }

  const handleFailure = (error: any) => {
    displayErrorMessage(error)
    setProcessing(false)
  }

  const handleSuccess = () => {
    clearColumnSelection()
    handleClose()
  }

  const handleDeleteColumns = () => {
    if (count === 0) return
    setProcessing(true)
    void deleteTableColumnsAction(
      tableId,
      columnIds,
      project.publicId,
      setSpreadsheetData,
      handleSuccess,
      handleFailure
    )
  }

  useEffect(() => {
    // Derive the ids from the string key (not the array prop) so the array's identity is never a
    // dependency — everything the effect reads is listed below, so no exhaustive-deps suppression.
    const columnIdsToCheck = columnIdsKey ? columnIdsKey.split(',') : []
    if (!open || columnIdsToCheck.length === 0) return
    setLoadingViewUsedColumns(true)
    // Union the affected views across every column being deleted, de-duplicated by publicId.
    Promise.all(columnIdsToCheck.map((columnId) => api.getColumnUsedViews(tableId, columnId)))
      .then((responses) => {
        const viewsById = new Map<string, ITableViewWithColumns>()
        responses.forEach((response) => response.data.forEach((view) => viewsById.set(view.publicId, view)))
        setUsedViews([...viewsById.values()])
        setLoadingViewUsedColumns(false)
      })
      .catch((error) => {
        displayErrorMessage(error)
        setLoadingViewUsedColumns(false)
      })
  }, [open, columnIdsKey, tableId, displayErrorMessage])

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Confirm Deletion">
      Are you sure you want to delete {isSingle ? 'this column' : `these ${count} columns`}? Filters, sorts, groupings,
      as well as the column{isSingle ? '' : 's'} {isSingle ? 'itself' : 'themselves'}, will be removed from the
      following views in this table:
      <ul className="overflow-y-auto mt-5 p-5 min-h-[200px] max-h-[200px]">
        {loadingViewUsedColumns && (
          <div>
            <li className="mb-[5px]">
              <Skeleton className="w-[100px] h-5" />
            </li>
            <li className="mb-[5px]">
              <Skeleton className="w-[100px] h-5" />
            </li>
            <li className="mb-[5px]">
              <Skeleton className="w-[100px] h-5" />
            </li>
          </div>
        )}

        {!loadingViewUsedColumns &&
          [...usedViews]
            .sort((a: ITableViewWithColumns, b: ITableViewWithColumns) =>
              (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
            )
            .map((view: ITableViewWithColumns, viewNumber: number) => {
              return (
                <li className="mb-[5px]" key={viewNumber}>
                  <a
                    href={`${HUB_BASE}${project.publicId}/table/${spreadsheetData.tableDetails.publicId}/view/${view.publicId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {view.name}
                  </a>
                </li>
              )
            })}
      </ul>
      <div className="flex justify-end mt-[30px]">
        <Button
          className="mr-[15px] min-w-[170px] max-w-[170px]"
          internalType="secondary"
          onClick={() => {
            if (!processing) setOpen(false)
          }}
          disabled={processing || loadingViewUsedColumns}
        >
          Keep Column{isSingle ? '' : 's'}
        </Button>
        <Button
          internalType="danger"
          className="min-w-[170px] max-w-[170px]"
          onClick={() => {
            if (!processing) handleDeleteColumns()
          }}
          disabled={processing || loadingViewUsedColumns}
          isLoading={processing}
        >
          {isSingle ? 'Delete Column' : `Delete ${count} Columns`}
        </Button>
      </div>
    </Modal>
  )
}

export default DeleteColumnModal
